import { useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { PageHeader, StatusPill } from "@/components/admin/PageHeader";
import { adminCollectionsQuery } from "@/lib/collections/queries";
import {
  deleteCollectionsFn,
  duplicateCollectionFn,
  setCollectionStatusFn,
} from "@/lib/collections/collections.functions";
import { COLLECTION_STATUSES, type AdminCollection, type CollectionStatus } from "@/lib/collections/types";

export const Route = createFileRoute("/admin/collections/")({ component: CollectionsAdminPage });

const TONE: Record<CollectionStatus, "success" | "warn" | "info" | "neutral"> = {
  published: "success",
  scheduled: "info",
  draft: "warn",
  archived: "neutral",
};

function fmtDate(v: string | null) {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function CollectionsAdminPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data = [], isLoading, error } = useQuery(adminCollectionsQuery());
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<"all" | CollectionStatus>("all");
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);

  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    return data.filter((c) => {
      if (status !== "all" && c.status !== status) return false;
      if (featuredOnly && !c.isFeatured) return false;
      if (!term) return true;
      return (c.name + " " + c.slug + " " + c.shortDescription).toLowerCase().includes(term);
    });
  }, [data, q, status, featuredOnly]);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["admin", "collections"] });
    setSelected([]);
  };

  const statusMut = useMutation({
    mutationFn: (v: { ids: string[]; status: CollectionStatus }) => setCollectionStatusFn({ data: v }),
    onSuccess: (_r, v) => {
      toast.success(`${v.ids.length} collection${v.ids.length === 1 ? "" : "s"} marked ${v.status}`);
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMut = useMutation({
    mutationFn: (ids: string[]) => deleteCollectionsFn({ data: { ids } }),
    onSuccess: () => { toast.success("Collection(s) deleted"); invalidate(); },
    onError: (e: Error) => toast.error(e.message),
  });

  const duplicateMut = useMutation({
    mutationFn: (id: string) => duplicateCollectionFn({ data: { id } }),
    onSuccess: () => { toast.success("Collection duplicated as draft"); invalidate(); },
    onError: (e: Error) => toast.error(e.message),
  });

  function toggle(id: string) {
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  }
  function toggleAll() {
    setSelected((s) => (s.length === rows.length ? [] : rows.map((r) => r.id)));
  }

  function exportCsv() {
    const head = [
      "Name","Slug","Status","Featured","Display Order","Parent","Products","Short Description","SEO Title","Created","Updated",
    ];
    const nameById = new Map(data.map((c) => [c.id, c.name]));
    const esc = (v: string | number) => `"${String(v).replace(/"/g, '""')}"`;
    const lines = [head.map(esc).join(",")];
    for (const c of rows) {
      lines.push([
        c.name, c.slug, c.status, c.isFeatured ? "Yes" : "No", c.displayOrder,
        c.parentId ? (nameById.get(c.parentId) ?? "") : "", c.productCount,
        c.shortDescription, c.seo.title, fmtDate(c.createdAt), fmtDate(c.updatedAt),
      ].map(esc).join(","));
    }
    const url = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `cb16-collections-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const busy = statusMut.isPending || deleteMut.isPending || duplicateMut.isPending;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Collections"
        subtitle={`${data.length} collections · ${data.filter((c) => c.status === "published").length} published`}
        action={
          <button
            type="button"
            onClick={() => navigate({ to: "/admin/collections/$id", params: { id: "new" } })}
            className="border border-black bg-black px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-white hover:border-[#C6A15B] hover:bg-[#C6A15B]"
          >
            Add collection
          </button>
        }
      />

      <div className="flex flex-wrap items-center gap-3 border border-black/10 bg-white p-3">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name, slug, description…"
          className="min-w-[220px] flex-1 border border-black/15 px-3 py-2 text-sm outline-none focus:border-[#C6A15B]"
        />
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as "all" | CollectionStatus)}
          className="border border-black/15 px-3 py-2 text-sm outline-none focus:border-[#C6A15B]"
        >
          <option value="all">All statuses</option>
          {COLLECTION_STATUSES.map((s) => (
            <option key={s} value={s}>{s[0]!.toUpperCase() + s.slice(1)}</option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-black/60">
          <input type="checkbox" checked={featuredOnly} onChange={(e) => setFeaturedOnly(e.target.checked)} />
          Featured
        </label>
        <button type="button" onClick={exportCsv} className="border border-black/15 px-3 py-2 text-[11px] font-semibold uppercase tracking-widest hover:border-[#C6A15B]">
          Export CSV
        </button>
      </div>

      {selected.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 border border-[#C6A15B]/40 bg-[#C6A15B]/5 p-3 text-xs">
          <span className="font-semibold uppercase tracking-widest text-black/70">{selected.length} selected</span>
          <button type="button" disabled={busy} onClick={() => statusMut.mutate({ ids: selected, status: "published" })} className="border border-black/15 bg-white px-3 py-1.5 font-semibold uppercase tracking-widest hover:border-[#C6A15B]">Publish</button>
          <button type="button" disabled={busy} onClick={() => statusMut.mutate({ ids: selected, status: "draft" })} className="border border-black/15 bg-white px-3 py-1.5 font-semibold uppercase tracking-widest hover:border-[#C6A15B]">Unpublish</button>
          <button type="button" disabled={busy} onClick={() => statusMut.mutate({ ids: selected, status: "archived" })} className="border border-black/15 bg-white px-3 py-1.5 font-semibold uppercase tracking-widest hover:border-[#C6A15B]">Archive</button>
          <button
            type="button"
            disabled={busy}
            onClick={() => { if (confirm(`Delete ${selected.length} collection(s)? This cannot be undone.`)) deleteMut.mutate(selected); }}
            className="border border-red-300 bg-white px-3 py-1.5 font-semibold uppercase tracking-widest text-red-700 hover:border-red-500"
          >
            Delete
          </button>
        </div>
      )}

      {error && <p className="border border-red-200 bg-red-50 p-3 text-sm text-red-700">{(error as Error).message}</p>}

      <div className="overflow-x-auto border border-black/10 bg-white">
        <table className="w-full min-w-[980px] text-left text-sm">
          <thead className="border-b border-black/10 text-[10px] uppercase tracking-widest text-black/50">
            <tr>
              <th className="px-3 py-3"><input type="checkbox" checked={rows.length > 0 && selected.length === rows.length} onChange={toggleAll} /></th>
              <th className="px-3 py-3">Collection</th>
              <th className="px-3 py-3">Status</th>
              <th className="px-3 py-3">Featured</th>
              <th className="px-3 py-3">Order</th>
              <th className="px-3 py-3">Parent</th>
              <th className="px-3 py-3">Products</th>
              <th className="px-3 py-3">Updated</th>
              <th className="px-3 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={9} className="px-3 py-8 text-center text-black/50">Loading collections…</td></tr>}
            {!isLoading && rows.length === 0 && <tr><td colSpan={9} className="px-3 py-8 text-center text-black/50">No collections match your filters.</td></tr>}
            {rows.map((c: AdminCollection) => (
              <tr key={c.id} className="border-b border-black/5 align-top">
                <td className="px-3 py-3"><input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggle(c.id)} /></td>
                <td className="px-3 py-3">
                  <div className="flex items-center gap-3">
                    {c.thumbnailImage || c.bannerImage ? (
                      <img src={c.thumbnailImage || c.bannerImage} alt="" className="h-10 w-10 shrink-0 object-cover" />
                    ) : (
                      <span className="h-10 w-10 shrink-0 bg-black/5" />
                    )}
                    <div>
                      <Link to="/admin/collections/$id" params={{ id: c.id }} className="font-semibold text-black hover:text-[#C6A15B]">{c.name}</Link>
                      <p className="text-xs text-black/50">/{c.slug}</p>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-3">
                  <StatusPill tone={TONE[c.status]}>{c.status}</StatusPill>
                  {c.status === "scheduled" && c.scheduledAt && <p className="mt-1 text-[11px] text-black/50">{fmtDate(c.scheduledAt)}</p>}
                </td>
                <td className="px-3 py-3 text-black/70">{c.isFeatured ? "Yes" : "—"}</td>
                <td className="px-3 py-3 text-black/70">{c.displayOrder}</td>
                <td className="px-3 py-3 text-black/70">{c.parentId ? (data.find((p) => p.id === c.parentId)?.name ?? "—") : "—"}</td>
                <td className="px-3 py-3 text-black/70">{c.productCount}</td>
                <td className="px-3 py-3 text-black/60">{fmtDate(c.updatedAt)}</td>
                <td className="px-3 py-3">
                  <div className="flex flex-wrap justify-end gap-2 text-[10px] font-semibold uppercase tracking-widest">
                    <Link to="/admin/collections/$id" params={{ id: c.id }} className="border border-black/15 px-2 py-1 hover:border-[#C6A15B]">Edit</Link>
                    <button type="button" disabled={busy} onClick={() => duplicateMut.mutate(c.id)} className="border border-black/15 px-2 py-1 hover:border-[#C6A15B]">Duplicate</button>
                    {c.status === "published" ? (
                      <button type="button" disabled={busy} onClick={() => statusMut.mutate({ ids: [c.id], status: "draft" })} className="border border-black/15 px-2 py-1 hover:border-[#C6A15B]">Unpublish</button>
                    ) : (
                      <button type="button" disabled={busy} onClick={() => statusMut.mutate({ ids: [c.id], status: "published" })} className="border border-black/15 px-2 py-1 hover:border-[#C6A15B]">Publish</button>
                    )}
                    <button type="button" disabled={busy} onClick={() => { if (confirm(`Delete "${c.name}"?`)) deleteMut.mutate([c.id]); }} className="border border-red-200 px-2 py-1 text-red-700 hover:border-red-500">Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
